import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {SupportSlotsController} from '../../../../api/support/support-slots.controller';
import {SupportSlotAssignmentTypeEnum} from '../../../../model/support/slot/support-slot-assignment-type.enum';
import {SupportSlotDefinitionCustomerAssignmentModel} from '../../../../model/support/slot/support-slot-definition-customer-assignment.model';
import {SupportSlotDefinitionModel} from '../../../../model/support/slot/support-slot-definition.model';
import {SupportCustomerPlanModel} from '../../../../model/support/support-customer-plan.model';
import {SupportPlanTask} from '../../../../model/support/task/support-plan-task';
import {
  SearchableState,
  useSearchableState,
} from '../../../../state/searchable-state';
import {SupportPlanPageHooks,} from './support-plan-page-hooks';

export type CreateSupportPlanTaskContext = {
  startDate: string
  endDate: string
  slot: SupportSlotDefinitionCustomerAssignmentModel
}

export type SupportPlanPageState = {
  viewMode: 'CALENDAR' | 'LIST'
  onViewModeChanged: () => void
  translate: (key: string) => string
  translateLocal: (key: string) => string
  supportPlan: SupportCustomerPlanModel | null

  slotDefinitionAssignments: SupportSlotDefinitionCustomerAssignmentModel[]
  slotDefinitions: SupportSlotDefinitionModel[]

  drawerOpen: boolean
  setDrawerOpen: (value: boolean) => void

  createTaskDrawerOpen: boolean
  setCreateTaskDrawerOpen: (value: boolean, context: CreateSupportPlanTaskContext | null) => void
  createTaskContext: CreateSupportPlanTaskContext | null

  slotPreviewDrawerOpen: boolean
  setSlotPreviewDrawerOpen: (value: boolean, slotDefinitionId: string | null) => void
  selectedSlotDefinitionAssignment: SupportSlotDefinitionCustomerAssignmentModel | null
  onSlotAssignmentDeleted: (slotDefinitionAssignmentId: string, version: number) => Promise<void>

  taskPreviewDrawerOpen: boolean
  setTaskPreviewDrawerOpen: (value: boolean, taskId: string | null) => void
  selectedTask: SupportPlanTask | null

  extractSlotAssignmentType: (slotDefinitionId: string) => SupportSlotAssignmentTypeEnum | null

  refreshSupportPlan: () => Promise<void>
  refreshTasks: () => Promise<void>

  tasks: SupportPlanTask[]
  searchableState: SearchableState
  extractAvailableValues: (column: string) => string[]
  total: number

  onCalendarDateRangeChanged: (startDate: string, endDate: string) => void
}

export const useSupportPlanPageState = (organizationId: string): SupportPlanPageState => {
  const {t} = useTranslation()
  const [viewMode, setViewMode] = useState<'CALENDAR' | 'LIST'>('CALENDAR');

  const [supportPlan, setSupportPlan] = SupportPlanPageHooks.useCustomerSupportPlan(organizationId)
  const [slotDefinitionAssignments, setSlotDefinitionAssignments] = SupportPlanPageHooks.useCustomerSlotDefinitions(organizationId)
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
  const [slotPreviewDrawerOpen, setSlotPreviewDrawerOpen] = useState<boolean>(false)
  const [selectedSlotDefinitionAssignment, setSelectedSlotDefinitionAssignment] = useState<SupportSlotDefinitionCustomerAssignmentModel | null>(null)
  const [createTaskDrawerOpen, setCreateTaskDrawerOpen] = useState<boolean>(false)
  const [createTaskContext, setCreateTaskContext] = useState<CreateSupportPlanTaskContext | null>(null)
  const [taskPreviewDrawerOpen, setTaskPreviewDrawerOpen] = useState<boolean>(false)
  const [selectedTask, setSelectedTask] = useState<SupportPlanTask | null>(null)

  const availableValues = ['support_plan_task.task_type']
  const fullTextSearchColumns = ['support_plan_task.id']

  const searchableState = useSearchableState(availableValues, fullTextSearchColumns)
  const [supportPlanTasks, setSupportPlanTasks] = SupportPlanPageHooks.useCustomerSupportPlanTasks(organizationId, supportPlan?.id ?? null, searchableState.searchable)

  const refreshSupportPlan = async () => {
    setSupportPlan(await SupportPlanPageHooks.fetchCustomerSupportPlan(organizationId))
  }

  const refreshSlotDefinitionAssignments = async () => {
    setSlotDefinitionAssignments(await SupportPlanPageHooks.fetchCustomerSlotDefinitions(organizationId))
  }

  const refreshTasks = async () => {
    setSupportPlanTasks(await SupportPlanPageHooks.fetchCustomerSupportPlanTasks(organizationId, supportPlan!.id, searchableState.searchable))
  }

  return {
    viewMode: viewMode,
    onViewModeChanged: () => {
      const newViewMode = viewMode === 'LIST' ? 'CALENDAR' : 'LIST';
      if (newViewMode === 'CALENDAR') {
        searchableState.onPage(1);
        searchableState.onPageSize(1000);
        searchableState.clearFilters()
      }
      if (newViewMode === 'LIST') {
        searchableState.onPage(1);
        searchableState.onPageSize(20);
        searchableState.clearFilters()
      }
      setViewMode(newViewMode);
    },
    onCalendarDateRangeChanged: (startDate, endDate) => {
      searchableState.onFilters([{
        column: 'support_plan_task.end_date',
        searchOperator: 'GREATER_OR_EQUAL',
        values: [startDate]
      }, {
        column: 'support_plan_task.start_date',
        searchOperator: 'LESSER_OR_EQUAL',
        values: [endDate]
      }]);
    },
    tasks: supportPlanTasks?.items ?? [],
    total: supportPlanTasks?.total ?? 0,
    searchableState: searchableState,
    extractAvailableValues: (column: string) => {
      return searchableState.extractAvailableValues(supportPlanTasks?.available ?? {}, column)
    },
    taskPreviewDrawerOpen: taskPreviewDrawerOpen,
    setTaskPreviewDrawerOpen: (value, taskId) => {
      setTaskPreviewDrawerOpen(value)
      const task = supportPlanTasks?.items?.find(it => it.id === taskId) ?? null
      setSelectedTask(task)
    },
    selectedTask: selectedTask,
    supportPlan: supportPlan,
    drawerOpen: drawerOpen,
    createTaskDrawerOpen: createTaskDrawerOpen,
    setCreateTaskDrawerOpen: (value, context) => {
      setCreateTaskDrawerOpen(value);
      setCreateTaskContext(context)
    },
    createTaskContext: createTaskContext,
    slotPreviewDrawerOpen: slotPreviewDrawerOpen,
    setSlotPreviewDrawerOpen: (value, slotDefinitionId) => {
      setSlotPreviewDrawerOpen(value)
      const slotDefinitionAssignment = slotDefinitionAssignments.find(it => it.slotDefinitionId === slotDefinitionId) ?? null
      setSelectedSlotDefinitionAssignment(slotDefinitionAssignment)
    },
    extractSlotAssignmentType: (slotDefinitionId) => slotDefinitionAssignments.find(it => it.slotDefinitionId === slotDefinitionId)?.assignmentType ?? null,
    selectedSlotDefinitionAssignment: selectedSlotDefinitionAssignment,
    slotDefinitionAssignments: slotDefinitionAssignments,
    slotDefinitions: slotDefinitionAssignments.map(it => ({
      id: it.slotDefinitionId,
      weekday: it.weekday,
      startTime: it.startTime,
      endTime: it.endTime,
      validFrom: it.validFrom,
      validTo: it.validTo,
      isGlobal: it.isGlobal,
      isActive: it.isActive,
      version: it.slotDefinitionVersion
    })),
    onSlotAssignmentDeleted: async (slotDefinitionAssignmentId, version) => {
      await SupportSlotsController.deleteSupportSlotDefinitionAssignment(slotDefinitionAssignmentId, version)
      await refreshSlotDefinitionAssignments()
    },
    refreshTasks: refreshTasks,
    refreshSupportPlan: refreshSupportPlan,
    setDrawerOpen: setDrawerOpen,
    translate: (key) => t(key),
    translateLocal: (key) => t(`support-plan-page.${key}`)
  }
}